// ============================================
// pages/manager-procurement/ManagerProcurementDashboard.tsx
// Manager Procurement approves DM-certified gunny utilization rows.
// Defaults AI–AN to DM-certified values; can override bags + rate before approving.
// ============================================
import React, { useEffect, useState, useCallback } from 'react';
import { AlertCircle, CheckCircle, Check, Eye } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { reportsAPI, gunnyUtilizationAPI, seasonsAPI, GunnyProformaRow } from '../../api/services';
import { GunnyStepTracker } from '../../components/gunny/GunnyStepTracker';
import { GunnyRowDetail } from '../../components/gunny/GunnyRowDetail';
import { GunnyFilterBar, applyFilters, emptyFilter, GunnyFilterState } from '../../components/gunny/GunnyFilterBar';

interface ApproveState {
  id: number;
  seasonId: number;
  district: string;
  merchant: string;
  society: string | null;
  dm_certified_bags: number;
  dm_rate: number;
  dm_gross: number;
  mp_certified_bags: number;
  mp_rate_per_bag_incl_gst: number;
}

export const ManagerProcurementDashboard: React.FC = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState<GunnyProformaRow[]>([]);
  const [seasonId, setSeasonId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [approve, setApprove] = useState<ApproveState | null>(null);
  const [detail, setDetail] = useState<GunnyProformaRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'all'>('pending');
  const [filters, setFilters] = useState<GunnyFilterState>(emptyFilter);

  const load = useCallback(async (sid: number) => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await reportsAPI.gunniesProforma({ season_id: sid });
      setRows(data.rows);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Failed to load proforma');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await seasonsAPI.active();
        if (!data?.id) {
          setErr('No active season configured');
          setLoading(false);
          return;
        }
        setSeasonId(data.id);
        load(data.id);
      } catch (e: any) {
        setErr(e?.response?.data?.message || 'Failed to load the active season');
        setLoading(false);
      }
    })();
  }, [load]);

  const baseVisible = statusFilter === 'pending'
    ? rows.filter((r) => r.status === 'submitted' || r.status === 'dm_verified')
    : rows;
  const visible = applyFilters(baseVisible, filters);

  const openApprove = (r: GunnyProformaRow) => {
    if (!seasonId) return;
    setApprove({
      id: r.id,
      seasonId,
      district: r.district,
      merchant: r.merchant,
      society: r.society_name,
      dm_certified_bags: r.certified_bags,
      dm_rate: r.rate_per_bag_incl_gst || 0,
      dm_gross: r.gross_amount || 0,
      mp_certified_bags: r.certified_bags,
      mp_rate_per_bag_incl_gst: r.rate_per_bag_incl_gst || 0,
    });
  };

  const submit = async () => {
    if (!approve) return;
    setSaving(true);
    setErr(null);
    try {
      await gunnyUtilizationAPI.mpApprove(approve.seasonId, approve.id, {
        mp_certified_bags: approve.mp_certified_bags,
        mp_rate_per_bag_incl_gst: approve.mp_rate_per_bag_incl_gst,
      });
      setFlash('Approved. CAO can now release payment.');
      setApprove(null);
      if (seasonId) await load(seasonId);
      setTimeout(() => setFlash(null), 2500);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Approve failed');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (v: number | null) =>
    v == null ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Manager Procurement — Approvals</h1>
          <p className="text-sm text-gray-600">
            Scrutinize DM-certified gunny utilization bills and approve them for payment. Values default to DM's
            certified numbers; override bags or rate only when you have a reason to.
          </p>
        </div>
        <div className="flex gap-1 bg-white border rounded p-1 text-xs">
          <button
            onClick={() => setStatusFilter('pending')}
            className={`px-3 py-1 rounded ${statusFilter === 'pending' ? 'bg-indigo-600 text-white' : 'text-gray-600'}`}
          >
            Pending ({rows.filter((r) => r.status === 'submitted' || r.status === 'dm_verified').length})
          </button>
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1 rounded ${statusFilter === 'all' ? 'bg-indigo-600 text-white' : 'text-gray-600'}`}
          >
            All ({rows.length})
          </button>
        </div>
      </div>

      <GunnyFilterBar rows={rows} value={filters} onChange={setFilters} />

      {err && (
        <div className="mb-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}
      {flash && (
        <div className="mb-3 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
          <CheckCircle className="w-4 h-4" /> {flash}
        </div>
      )}

      <div className="bg-white rounded-xl shadow border overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            {statusFilter === 'pending' ? 'No rows awaiting your approval.' : 'No rows in this season.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-xs uppercase text-gray-600">
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">District</th>
                  <th className="px-3 py-2">Supplier</th>
                  <th className="px-3 py-2">Society</th>
                  <th className="px-3 py-2 text-right">DM bags</th>
                  <th className="px-3 py-2 text-right">Rate</th>
                  <th className="px-3 py-2 text-right">DM Gross</th>
                  <th className="px-3 py-2 text-right">MP Gross</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-500">{r.sl_no}</td>
                    <td className="px-3 py-2">{r.district}</td>
                    <td className="px-3 py-2 truncate max-w-[220px]">{r.merchant}</td>
                    <td className="px-3 py-2">{r.society_name || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.certified_bags.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(r.rate_per_bag_incl_gst)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(r.gross_amount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(r.mp_gross_amount)}</td>
                    <td className="px-3 py-2">
                      <GunnyStepTracker row={r} />
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => setDetail(r)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded mr-1"
                      >
                        <Eye className="w-3 h-3" /> View
                      </button>
                      {r.status === 'submitted' || r.status === 'dm_verified' ? (
                        <button
                          onClick={() => openApprove(r)}
                          className="inline-flex items-center gap-1 px-3 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded"
                        >
                          <Check className="w-3 h-3" /> Approve
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">{r.status === 'manager_approved' ? 'approved' : r.status}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-3 text-xs text-gray-500">
        Signed in as <span className="font-medium">{user?.name}</span>.
      </div>

      {approve && (
        <ApproveModal state={approve} setState={setApprove} submit={submit} saving={saving} onClose={() => setApprove(null)} />
      )}

      <GunnyRowDetail
        row={detail}
        onClose={() => setDetail(null)}
        onRowUpdated={() => { if (seasonId) load(seasonId); }}
      />
    </div>
  );
};

const ApproveModal: React.FC<{
  state: ApproveState;
  setState: (s: ApproveState) => void;
  submit: () => void;
  saving: boolean;
  onClose: () => void;
}> = ({ state, setState, submit, saving, onClose }) => {
  const mpGross = +(state.mp_certified_bags * state.mp_rate_per_bag_incl_gst || 0).toFixed(2);
  const cgst = +(mpGross / 21 / 2).toFixed(2);
  const net = +(mpGross - cgst * 2).toFixed(2);
  const changed =
    state.mp_certified_bags !== state.dm_certified_bags
    || state.mp_rate_per_bag_incl_gst !== state.dm_rate;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
        <div className="px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">Approve for payment</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {state.district} · {state.merchant} · {state.society || '—'}
          </p>
        </div>
        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <div className="col-span-2 p-3 bg-gray-50 border rounded">
            <p className="text-xs text-gray-500 mb-1">District Manager's certification</p>
            <div className="flex justify-between text-sm">
              <span>{state.dm_certified_bags.toLocaleString()} bags × ₹{state.dm_rate.toFixed(2)}</span>
              <span className="font-semibold tabular-nums">₹ {state.dm_gross.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your certified bags</label>
            <input
              type="number"
              min={0}
              value={state.mp_certified_bags}
              onChange={(e) => setState({ ...state, mp_certified_bags: Number(e.target.value) || 0 })}
              className="w-full px-3 py-2 border rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your rate per bag (incl. GST)</label>
            <input
              type="number"
              step="0.01"
              min={0}
              value={state.mp_rate_per_bag_incl_gst}
              onChange={(e) => setState({ ...state, mp_rate_per_bag_incl_gst: Number(e.target.value) || 0 })}
              className="w-full px-3 py-2 border rounded text-sm"
            />
          </div>

          <div className="col-span-2 p-3 border-t pt-3 text-sm bg-indigo-50 rounded">
            <div className="flex justify-between">
              <span className="text-gray-700">Your gross</span>
              <span className="tabular-nums font-semibold">₹ {mpGross.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between text-xs text-gray-600 mt-0.5">
              <span>CGST ≈ SGST</span>
              <span className="tabular-nums">₹ {cgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })} each</span>
            </div>
            <div className="flex justify-between text-xs text-gray-600 mt-0.5">
              <span>Net</span>
              <span className="tabular-nums">₹ {net.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            {changed && (
              <p className="text-xs text-amber-700 mt-2">You are overriding the DM's certified values.</p>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">Cancel</button>
          <button
            onClick={submit}
            disabled={saving || state.mp_certified_bags <= 0 || state.mp_rate_per_bag_incl_gst <= 0}
            className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded disabled:opacity-50"
          >
            {saving ? 'Approving…' : 'Approve for payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManagerProcurementDashboard;
